import { AlertTriangle, CheckCircle2, Package, Wrench } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatQty, type ReservationSummary, type TransitionMode } from '@/lib/process/allocation'

/** The question the signature answers. Also the sign dialog's own title. */
export const TRANSITION_HEADING: Record<TransitionMode, string> = {
  start: 'Start this process step?',
  end: 'Stop this process step?',
  complete: 'Complete this process step?',
}

/**
 * The transition, spelled out. The arrow is the status change the audit trail
 * will record, not a description of the button.
 */
const TRANSITION: Record<TransitionMode, string> = {
  start: 'Assigned → Started',
  end: 'Started → Pending',
  complete: 'Pending → Completed',
}

const TIMING: Record<TransitionMode, string> = {
  // The server stamps the real moment in every case. A typed time is a plan,
  // and the trail must not record it as the observed one.
  start: 'The actual start time is stamped when you sign.',
  end: 'The actual stop time is stamped when you sign.',
  complete: 'The completion time is stamped when you sign.',
}

function Row({ icon: Icon, children }: { icon: typeof Wrench; children: React.ReactNode }) {
  return (
    <li className='flex items-start gap-2 text-sm text-gray-700'>
      <Icon className='mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400' />
      <span className='min-w-0'>{children}</span>
    </li>
  )
}

/**
 * What signing is about to put on the record.
 *
 * This screen exists because the signature used to sit in a footer bar a
 * screen-height below the button that consumed it, with no statement anywhere
 * of what was being attested. An operator signed a name into a box; they did
 * not see a claim they were agreeing to. Here the claim comes first and the
 * signature is the last thing on it.
 */
export function TransitionConfirmScreen({
  mode,
  summary,
  kit,
  stepName,
  expectedStart,
  equipmentHeldFrom = null,
  alreadyHeld = false,
}: {
  mode: TransitionMode
  summary: ReservationSummary
  /** Kit runs carry an identity and a derived expiry worth restating here. */
  kit?: { supplyId: string | null; expiry: string | null } | null
  /** The step being started, named in the commitment statement. */
  stepName?: string
  /** Formatted expected start, or null when the operator left it blank. */
  expectedStart?: string | null
  /**
   * Formatted start of the EARLIEST equipment hold, or null when nothing is
   * held. Each requirement carries its own prep, so this is the first moment
   * any unit is taken — not a single offset shared by all of them.
   */
  equipmentHeldFrom?: string | null
  /**
   * This step already holds equipment. Distinct from `holdsEquipment`, which
   * only says units are PICKED — a step can have picks and no holds, or holds
   * carried over from before the modal was opened with no local draft at all.
   */
  alreadyHeld?: boolean
}) {
  const unpicked = summary.equipment.filter(e => !e.unit).length
  const holdsEquipment = summary.equipment.some(e => !!e.unit)

  return (
    <div className='space-y-4'>
      {/* What confirming COSTS, said before the record is described. Signing
          here does not merely note that a step began: it takes stock out of
          everyone else's reach and books machines on a shared calendar. That is
          the part an operator cannot undo by closing the dialog. */}
      {mode === 'start' && (expectedStart || alreadyHeld || summary.equipment.length > 0) && (
        <div className='rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900'>
          {expectedStart ? (
            <>
              <p>
                Confirming locks the inventory below for
                {stepName ? <span className='font-semibold'> {stepName}</span> : ' this step'}
                {holdsEquipment && ', and reserves its equipment'}, based on an expected start of{' '}
                <span className='font-semibold'>{expectedStart}</span>.
              </p>
              {holdsEquipment && equipmentHeldFrom && equipmentHeldFrom !== expectedStart && (
                <p className='mt-1 text-xs text-blue-800'>
                  Equipment is held from {equipmentHeldFrom} — each unit is booked once its own
                  prep is done, so they may differ.
                </p>
              )}
            </>
          ) : alreadyHeld ? (
            // ALREADY BOOKED, so there is nothing left for an expected start to
            // arrange. Telling this operator "no equipment will be reserved"
            // was wrong on its face — their machines are held, and the sentence
            // read as though signing would lose them.
            <p>
              Confirming locks the inventory below for
              {stepName ? <span className='font-semibold'> {stepName}</span> : ' this step'}. Its
              equipment is <span className='font-semibold'>already reserved</span>
              {equipmentHeldFrom ? (
                <>
                  {' '}
                  from <span className='font-semibold'>{equipmentHeldFrom}</span>
                </>
              ) : null}{' '}
              and stays held.
            </p>
          ) : summary.equipment.length > 0 ? (
            // Only worth saying when this step actually has an equipment
            // requirement — otherwise "no equipment will be reserved" is a
            // caveat about something that was never in question.
            <p>
              Confirming locks the inventory below for
              {stepName ? <span className='font-semibold'> {stepName}</span> : ' this step'}.{' '}
              <span className='font-semibold'>No equipment will be reserved</span> — set an expected
              start before confirming if you want the machines held.
            </p>
          ) : null}
        </div>
      )}

      <div className='rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-3'>
        <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
          This will be recorded
        </p>

        <p className='mt-2 text-sm font-medium tabular-nums text-gray-900'>{TRANSITION[mode]}</p>
        <p className='mt-0.5 text-xs text-gray-500'>{TIMING[mode]}</p>

        {kit?.supplyId && (
          <p className='mt-2 text-sm text-gray-700'>
            Kit <span className='font-medium'>{kit.supplyId}</span>
            {kit.expiry ? (
              <span className='text-gray-500'> · expires {kit.expiry}</span>
            ) : (
              <span className='text-gray-500'> · expiry not yet determined</span>
            )}
          </p>
        )}

        {summary.equipment.length > 0 && (
          <ul className='mt-3 space-y-1'>
            {summary.equipment.map(e => (
              <Row key={e.requirementId} icon={Wrench}>
                {e.label}
                {e.unit ? (
                  <span className='font-medium text-gray-900'> — {e.unit}</span>
                ) : (
                  <span className='text-amber-700'> — no unit selected</span>
                )}
              </Row>
            ))}
          </ul>
        )}

        {summary.inventory.length > 0 && (
          <ul className='mt-3 space-y-1'>
            {summary.inventory.map(i => (
              <Row key={i.requirementId} icon={Package}>
                {i.label}
                <span className='font-medium text-gray-900'>
                  {' '}
                  — {formatQty(i.allocated, i.uom)}
                </span>
                <span className='text-gray-500'>
                  {' '}
                  from {i.lots.map(l => `${l.lot} (${formatQty(l.qty, i.uom)})`).join(', ')}
                </span>
              </Row>
            ))}
          </ul>
        )}

        {summary.equipment.length === 0 && summary.inventory.length === 0 && (
          <p className='mt-3 text-xs italic text-gray-400'>
            No equipment or stock is being committed by this step.
          </p>
        )}

        {summary.stockEffect && (
          <p
            className={cn(
              'mt-3 flex items-start gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium',
              mode === 'start' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800',
            )}
          >
            <CheckCircle2 className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {summary.stockEffect}
          </p>
        )}

        {/* Starting with a requirement unfilled is allowed — a run starts with
            an assignee and nothing else, and the operator records what it used
            as they go. Allowed is not the same as unremarkable, so it is said
            here rather than discovered later. */}
        {mode === 'start' && unpicked > 0 && (
          <p className='mt-3 flex items-start gap-1.5 text-xs text-amber-700'>
            <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {unpicked} equipment requirement{unpicked === 1 ? '' : 's'} without a unit. You can
            record {unpicked === 1 ? 'it' : 'them'} while the run is under way.
          </p>
        )}
      </div>
    </div>
  )
}
